/* eslint-disable sonarjs/cognitive-complexity, no-negated-condition */
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { object, string } from 'yup';
import {
  FORM_EMAIL,
  REGISTRATION_FIRST_NAME,
  REGISTRATION_LAST_NAME,
  DETAILS_BILLIND_ADDRESS,
  DETAILS_CHANGE_ADDRESS,
  BUTTON_CANCEL,
  DETAILS_UPDATE_ADDRESS,
  DETAILS_ADDRESS_IS_UPDATED,
  DETAILS_ADDRESS_UPDATE_ERROR,
  DETAILS_ADD_ADDRESS,
  REQUIRED_FIELD,
  REGISTER_ENTER_VALID_POSTCODE,
  DETAILS_CHANGE_POSTCODE,
  DETAILS_UPDATE_POSTCODE,
  DETAILS_ADD_POSTCODE,
  REGISTER_VERIFY_POSTCODE,
} from '../../../brands/default/content/components/checkout';
import { AUTO_ADDRESS_LOOKUP } from '../../../brands/default/features';
import { POSTCODE } from '../../../brands/fivepm/features';
import { verifyPostcode } from '../../../helpers/address';
import {
  getAutocompleteTestValue,
  updateUserDetails,
} from '../../../helpers/user';
import { validatePostCode } from '../../../helpers/validations';
import ThemeContext from '../../../providers/ThemeProvider';
import { updateCheckoutData } from '../../../redux/actions/checkout';
import { setLoadingState } from '../../../redux/actions/loading';
import { updateUser } from '../../../redux/actions/user';
import { LOADING_STATUS } from '../../../redux/reducers/loading';
import Button from '../../_generic/button/CTAButton';
import useToast from '../../_generic/toast/UseToast';
import BillingAddress from '../../address/BillingAddress';
import BillingAddressAutocomplete from '../../address/BillingAddressAutocomplete';
import BillingAddressOnlyPostcode from '../../address/BillingAddressOnlyPostcode';

const schema = (isFullAddressRequired, isManualAddress) =>
  object().shape({
    addressLine1: isFullAddressRequired
      ? string().required(REQUIRED_FIELD)
      : string(),
    addressLine2: string(),
    city: isFullAddressRequired ? string().required(REQUIRED_FIELD) : string(),
    postCode: !AUTO_ADDRESS_LOOKUP
      ? !isFullAddressRequired && !isManualAddress
        ? string()
            .required(REQUIRED_FIELD)
            .test('postCode', REGISTER_VERIFY_POSTCODE, (value) =>
              verifyPostcode(value),
            )
        : string()
            .required(REQUIRED_FIELD)
            .test('postCode', REGISTER_ENTER_VALID_POSTCODE, (value) =>
              validatePostCode(value),
            )
      : string(),
  });

const UserDetails = ({
  user,
  showAllFields = false,
  isHighFraudRiskDealInBasket = false,
  isPaymentRequiresAddress = false,
}) => {
  const userProfile = user.userprofile;
  const router = useRouter();
  const toast = useToast();
  const dispatch = useDispatch();
  const theme = useContext(ThemeContext);
  const autoCompleteTestCookie = getAutocompleteTestValue();
  const isUserInAutocompleteTest = autoCompleteTestCookie === 'b';
  const isUserInAutocompleteTestC = autoCompleteTestCookie === 'c';

  const showBillingAddress =
    userProfile &&
    (userProfile.addressLine1 || userProfile.town || userProfile.postcode);

  const isPostCode = POSTCODE ? true : userProfile.postcode;

  const initialValues = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    postCode: '',
  };
  const [isFullAddressRequired, setIsFullAddressRequired] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [accountIncomplete, setAccountIncomplete] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(initialValues);

  const handleManualAddress = (value, postcode) => {
    if (postcode) {
      initialValues.postCode = postcode;
      // if the form initialvalues changes and form has validateOnMount, validation is triggered immediatly
      setFormInitialValues(initialValues);
    }
    setIsManualAddress(value);
  };

  useEffect(() => {
    if (isUserInAutocompleteTest || isUserInAutocompleteTestC) {
      if (isUserInAutocompleteTest && userProfile.postcode) {
        setIsFullAddressRequired(false);
        setAccountIncomplete(false);
      }
      if (isUserInAutocompleteTest && !isHighFraudRiskDealInBasket) {
        setIsFullAddressRequired(false);
        setAccountIncomplete(!isPostCode);
      }
      if (
        isHighFraudRiskDealInBasket ||
        userProfile.addressLine1 ||
        userProfile.town ||
        isPaymentRequiresAddress ||
        isUserInAutocompleteTestC
      ) {
        setIsFullAddressRequired(true);
        const addressCompleted =
          isPostCode && userProfile.addressLine1 && userProfile.town;
        setAccountIncomplete(!addressCompleted);
      }
    } else {
      setIsFullAddressRequired(true);
      const addressCompleted =
        isPostCode && userProfile.addressLine1 && userProfile.town;
      setAccountIncomplete(!addressCompleted);
    }
  }, [
    isHighFraudRiskDealInBasket,
    isPaymentRequiresAddress,
    user,
    isPostCode,
    userProfile,
    isUserInAutocompleteTest,
    isUserInAutocompleteTestC,
  ]);

  const sendForm = async (values, resetForm) => {
    dispatch(setLoadingState(LOADING_STATUS.LOADING));
    const bt = router.query.bt;
    dispatch(updateCheckoutData(bt));
    const data = { ...userProfile };
    data.postcode = values.postCode;
    data.addressLine1 = values.addressLine1;
    data.addressLine2 = values.addressLine2;
    data.town = values.city;

    const response = await updateUser(data);
    dispatch(setLoadingState(LOADING_STATUS.READY));
    if (response && response.status === (200 || 202)) {
      dispatch(updateUserDetails(response.data));
      // only way to get new card postcode field to update
      if (!isPaymentRequiresAddress) router.reload();
      toast.addToast(DETAILS_ADDRESS_IS_UPDATED, 'toast-success');
    } else {
      toast.addToast(DETAILS_ADDRESS_UPDATE_ERROR, 'toast-error');
    }

    setShowAddressForm(false);
    resetForm();
  };

  return (
    <>
      <ul className="row user-info">
        <li className="col-12 user-info__item user-info__email">
          <small>{FORM_EMAIL}</small>
          <div>{userProfile.email}</div>
        </li>
        <li className="col-6 user-info__item user-info__name">
          <small>{REGISTRATION_FIRST_NAME}</small>
          <div>{userProfile.firstName}</div>
        </li>
        <li className="col-6 user-info__item user-info__surname">
          <small>{REGISTRATION_LAST_NAME}</small>
          <div>{userProfile.lastName}</div>
        </li>
        {showBillingAddress && (
          <li className="col-12 user-info__item user-info__address">
            <small>{DETAILS_BILLIND_ADDRESS}</small>
            {userProfile.addressLine1 && <div>{userProfile.addressLine1}</div>}
            {userProfile.addressLine2 && <div>{userProfile.addressLine2}</div>}
            {userProfile.town && <div>{userProfile.town}</div>}
            {userProfile.postcode && <div>{userProfile.postcode}</div>}
          </li>
        )}
        {(showAddressForm || accountIncomplete) && (
          <li
            className={`col-12 user-info__item user-info__form ${
              accountIncomplete ? 'add-address' : ''
            }`}
          >
            <Formik
              enableReinitialize
              initialValues={formInitialValues}
              onSubmit={async (values, resetForm) =>
                sendForm(values, resetForm)
              }
              validateOnBlur
              validateOnChange
              validateOnMount
              validationSchema={schema(isFullAddressRequired, isManualAddress)}
            >
              {({
                handleChange,
                handleSubmit,
                touched,
                handleBlur,
                setValues,
                isSubmitting,
                isValid,
                values,
                errors,
              }) => {
                return (
                  <form
                    className="row"
                    name="update-address-form"
                    onSubmit={handleSubmit}
                  >
                    {isFullAddressRequired &&
                      !isUserInAutocompleteTest &&
                      !isUserInAutocompleteTestC && (
                        <BillingAddress
                          errors={errors}
                          onBlurFunction={handleBlur}
                          onChangeFunction={handleChange}
                          setValues={setValues}
                          showAllFields={showAllFields}
                          touched={touched}
                          values={values}
                        />
                      )}
                    {isFullAddressRequired &&
                      (isUserInAutocompleteTest ||
                        isUserInAutocompleteTestC) && (
                        <BillingAddressAutocomplete
                          errors={errors}
                          onBlurFunction={handleBlur}
                          onChangeFunction={handleChange}
                          setValues={setValues}
                          showAllFields={showAllFields}
                          touched={touched}
                          values={values}
                        />
                      )}
                    {!isFullAddressRequired && (
                      <BillingAddressOnlyPostcode
                        errors={errors}
                        onBlurFunction={handleBlur}
                        onChangeFunction={handleChange}
                        onManualAddress={handleManualAddress}
                        setValues={setValues}
                        showAllFields={showAllFields}
                        touched={touched}
                        values={values}
                      />
                    )}

                    <div className="col-12 d-flex justify-content-center">
                      {!accountIncomplete && (
                        <Button
                          className="change-address--cancel"
                          onClick={() => setShowAddressForm(false)}
                          primaryAction={false}
                          tertiaryButton
                        >
                          {BUTTON_CANCEL}
                        </Button>
                      )}

                      <Button
                        className="change-address--confirm gold-button"
                        disabled={isSubmitting || !isValid}
                        primaryAction={false}
                        type="submit"
                      >
                        {accountIncomplete
                          ? isFullAddressRequired
                            ? DETAILS_ADD_ADDRESS
                            : DETAILS_ADD_POSTCODE
                          : isFullAddressRequired
                          ? DETAILS_UPDATE_ADDRESS
                          : DETAILS_UPDATE_POSTCODE}
                      </Button>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </li>
        )}
        {!showAddressForm && !accountIncomplete && (
          <li className="col-12 user-info__item text-center">
            <Button
              className="change-address"
              onClick={() => setShowAddressForm(true)}
              primaryAction={false}
              tertiaryButton
            >
              {isFullAddressRequired
                ? DETAILS_CHANGE_ADDRESS
                : DETAILS_CHANGE_POSTCODE}
            </Button>
          </li>
        )}
      </ul>
      <style jsx>{`
        .user-info {
          list-style: none;
          margin-bottom: 0;
          padding: 0;
        }
        .user-info__item {
          margin: 0 0 15px;
        }
        small {
          color: #757575;
        }
        .add-address {
          padding-top: 10px;
          position: relative;
        }

        .add-address:after {
          content: '';
          border-top: 1px solid ${theme.colors.primary};
          position: absolute;
          top: 0;
          left: 15px;
          right: 15px;
        }
      `}</style>
      <style global jsx>{`
        .change-address--cancel {
          margin-right: 5px;
        }
      `}</style>
    </>
  );
};

UserDetails.propTypes = {
  isHighFraudRiskDealInBasket: PropTypes.bool,
  isPaymentRequiresAddress: PropTypes.bool,
  showAllFields: PropTypes.bool,
  user: PropTypes.object,
};

export default UserDetails;
